class QuizSteps {
  constructor(section) {
    this.section = section;
    this.answers = {};
    this.currentStep = 0;
    this.totalSteps = parseInt(section.dataset.totalSteps, 10);
    this.transitionMs = 360;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.transitioning = false;

    this.header = section.querySelector('#QuizHeader');
    this.backBtn = section.querySelector('#QuizBackBtn');
    this.progressEl = section.querySelector('#QuizProgress');
    this.stage = section.querySelector('#QuizStage');

    this.buildPills();
    this.init();
  }

  init() {
    if (this.section.querySelector('.quiz-posted-success')) {
      this.showStep('success');
      return;
    }
    this.showStep(0);
    this.bindEvents();
  }

  bindEvents() {
    this.section.querySelectorAll('[data-quiz-answer]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const { key, value } = btn.dataset;
        if (key) this.answers[key] = value;
        this.setStepSelection(btn);
        this.next();
      });
    });

    this.section.querySelectorAll('[data-quiz-next]').forEach((btn) => {
      btn.addEventListener('click', () => this.next());
    });

    const hwBtn = this.section.querySelector('[data-quiz-next-hw]');
    if (hwBtn) {
      const heightInput = this.section.querySelector('#QuizHeight');
      const weightInput = this.section.querySelector('#QuizWeight');

      const syncHwBtn = () => {
        hwBtn.disabled = !heightInput?.value.trim() || !weightInput?.value.trim();
      };

      hwBtn.disabled = true;
      heightInput?.addEventListener('input', syncHwBtn);
      weightInput?.addEventListener('input', syncHwBtn);

      hwBtn.addEventListener('click', () => {
        const height = heightInput?.value.trim();
        const weight = weightInput?.value.trim();
        if (height) this.answers.height = `${height} in`;
        if (weight) this.answers.weight = `${weight} lbs`;
        this.next();
      });
    }

    this.backBtn?.addEventListener('click', () => this.back());

    const contactStep = this.section.querySelector('[data-block-type="contact"]');
    const form = this.section.querySelector('#QuizContactForm');
    if (form) {
      this.setupContactValidation(form);
    }
  }

  setupContactValidation(form) {
    form.setAttribute('novalidate', '');

    const firstName  = form.querySelector('#QuizFirstName');
    const lastName   = form.querySelector('#QuizLastName');
    const email      = form.querySelector('#QuizContactEmail');
    const phone      = form.querySelector('#QuizPhone');
    const birthYear  = form.querySelector('#QuizBirthYear');
    const state      = form.querySelector('#QuizState');
    const smsConsent = form.querySelector('#QuizSmsConsent');

    [firstName, lastName].forEach((input) => {
      if (!input) return;
      input.addEventListener('input', () => {
        input.value = input.value.replace(/[^a-zA-ZÀ-ÿ\s'\-]/g, '');
        this.clearError(input);
      });
      input.addEventListener('blur', () => this.validateName(input));
    });

    if (email) {
      email.addEventListener('input', () => this.clearError(email));
      email.addEventListener('blur', () => this.validateEmail(email));
    }

    if (phone) {
      phone.addEventListener('input', () => {
        this.applyPhoneMask(phone);
        this.clearError(phone);
      });
      phone.addEventListener('blur', () => this.validatePhone(phone));
    }

    if (birthYear) {
      birthYear.addEventListener('input', () => {
        if (birthYear.value.length > 4) birthYear.value = birthYear.value.slice(0, 4);
        this.clearError(birthYear);
      });
      birthYear.addEventListener('blur', () => this.validateBirthYear(birthYear));
    }

    if (state) {
      state.addEventListener('change', () => this.clearError(state));
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const valid = [
        this.validateName(firstName),
        this.validateName(lastName),
        this.validateEmail(email),
        this.validatePhone(phone),
        this.validateBirthYear(birthYear),
        this.validateState(state),
      ].every(Boolean);

      if (!valid) {
        form.querySelector('.quiz-field__input--error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const contactData = {
        firstName:  firstName?.value.trim(),
        lastName:   lastName?.value.trim(),
        email:      email?.value.trim(),
        phone:      phone?.value.trim(),
        birthYear:  birthYear?.value.trim(),
        state:      state?.value || '',
        smsConsent: !!smsConsent?.checked,
      };

      if (typeof window.trsTrack === 'function') {
        window.trsTrack('generate_lead', {
          lead_source: 'sleep_assessment',
          form_id: 'sleep_assessment',
          has_phone: !!contactData.phone,
          sms_consent: contactData.smsConsent,
          value: 0,
          currency: 'USD',
        });
      }

      const submitBtn = form.querySelector('[type="submit"]');
      const submitLabel = submitBtn?.querySelector('span');
      const defaultSubmitText = submitLabel?.textContent || '';
      const errorEl = form.querySelector('.quiz-form-errors');

      const setSubmitting = (isSubmitting) => {
        if (!submitBtn) return;
        submitBtn.disabled = isSubmitting;
        if (submitLabel) {
          submitLabel.textContent = isSubmitting
            ? (form.dataset.sendingLabel || 'Sending...')
            : defaultSubmitText;
        }
      };

      const showError = (message) => {
        if (!errorEl) return;
        errorEl.hidden = false;
        errorEl.textContent = message;
      };

      // Klaviyo dual-write: fire-and-forget after the primary submit succeeds.
      // Never awaited, never blocks the primary path, never surfaces an error to the patient.
      const klaviyo = () => {
        try {
          this.submitToKlaviyo(contactData);
        } catch (err) {
          console.warn('[QuizSteps] Klaviyo dual-write skipped', err);
        }
      };

      this.populateFormFields();
      klaviyo();
            this.submitToSleepOps(contactData);
      form.submit();
    });
  }

  applyPhoneMask(input) {
    const digits = input.value.replace(/\D/g, '').slice(0, 10);
    if (digits.length >= 7) {
      input.value = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length >= 4) {
      input.value = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length > 0) {
      input.value = `(${digits}`;
    }
  }

  validateName(input) {
    if (!input) return true;
    const val = input.value.trim();
    if (!val) return this.setError(input, 'This field is required');
    if (val.length < 2) return this.setError(input, 'Must be at least 2 characters');
    this.clearError(input);
    return true;
  }

  validateEmail(input) {
    if (!input) return true;
    const val = input.value.trim();
    if (!val) return this.setError(input, 'Email is required');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return this.setError(input, 'Please enter a valid email address');
    this.clearError(input);
    return true;
  }

  validatePhone(input) {
    if (!input) return true;
    const val = input.value.trim();
    if (!val) return this.setError(input, 'Phone is required');
    if (val.replace(/\D/g, '').length < 10) return this.setError(input, 'Please enter a valid phone number');
    this.clearError(input);
    return true;
  }

  validateBirthYear(input) {
    if (!input) return true;
    const val = input.value.trim();
    if (!val) return this.setError(input, 'Birth year is required');
    const year = parseInt(val, 10);
    if (isNaN(year) || year < 1924 || year > new Date().getFullYear() - 10) {
      return this.setError(input, 'Please enter a valid birth year');
    }
    this.clearError(input);
    return true;
  }

  validateState(input) {
    if (!input) return true;
    if (!input.value) return this.setError(input, 'Please select your state');
    this.clearError(input);
    return true;
  }

  setError(input, message) {
    if (!input) return false;
    input.classList.add('quiz-field__input--error');
    let errorEl = input.parentElement.querySelector('.quiz-field__error');
    if (!errorEl) {
      errorEl = document.createElement('span');
      errorEl.className = 'quiz-field__error';
      input.after(errorEl);
    }
    errorEl.textContent = message;
    return false;
  }

  clearError(input) {
    if (!input) return;
    input.classList.remove('quiz-field__input--error');
    input.parentElement?.querySelector('.quiz-field__error')?.remove();
  }

  // A step with data-condition-key only shows when the referenced answer
  // matches data-condition-value (e.g. "Do you use a CPAP?" only after
  // diagnosed = Yes). Hidden steps record data-skip-answer, so downstream
  // traits stay populated with the implied answer.
  conditionMet(stepEl) {
    const key = stepEl.dataset.conditionKey;
    if (!key) return true;
    return this.answers[key] === stepEl.dataset.conditionValue;
  }

  recordSkipAnswer(stepEl) {
    const key = stepEl.dataset.answerKey;
    const skipAnswer = stepEl.dataset.skipAnswer;
    if (key && skipAnswer) this.answers[key] = skipAnswer;
  }

  next() {
    let step = this.currentStep + 1;
    let target = this.section.querySelector(`[data-step="${step}"]`);
    while (target && !this.conditionMet(target)) {
      this.recordSkipAnswer(target);
      step += 1;
      target = this.section.querySelector(`[data-step="${step}"]`);
    }
    this.showStep(step);
  }

  back() {
    let step = this.currentStep - 1;
    let target = this.section.querySelector(`[data-step="${step}"]`);
    while (step > 0 && target && !this.conditionMet(target)) {
      step -= 1;
      target = this.section.querySelector(`[data-step="${step}"]`);
    }
    if (step >= 0) this.showStep(step);
  }

  setStepSelection(selectedBtn) {
    const step = selectedBtn?.closest('.quiz-step');
    if (!step) return;

    step.querySelectorAll('[data-quiz-answer]').forEach((btn) => {
      btn.classList.toggle('is-selected', btn === selectedBtn);
      btn.setAttribute('aria-pressed', btn === selectedBtn ? 'true' : 'false');
    });
  }

  restoreStepSelection(stepEl) {
    if (!stepEl) return;

    const key = stepEl.dataset.answerKey;
    const saved = key ? this.answers[key] : undefined;

    stepEl.querySelectorAll('[data-quiz-answer]').forEach((btn) => {
      const isSelected = saved !== undefined && btn.dataset.value === saved;
      btn.classList.toggle('is-selected', isSelected);
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  showStep(step) {
    const incoming = this.section.querySelector(`[data-step="${step}"]`);
    if (!incoming || this.transitioning) return;

    const outgoing = this.section.querySelector('.quiz-step.is-active');
    if (outgoing === incoming) return;

    const activate = (incomingStep = incoming) => {
      this.section.querySelectorAll('.quiz-step').forEach((el) => {
        el.hidden = true;
        el.classList.remove('is-active', 'is-leaving');
      });

      incomingStep.hidden = false;
      incomingStep.classList.add('is-active');
      this.restoreStepSelection(incomingStep);

      if (typeof step === 'number') this.currentStep = step;

      const showHeader = typeof step === 'number' && step >= 1 && step <= this.totalSteps;
      this.header.hidden = !showHeader;
      if (showHeader) this.updateProgress(step);

      this.syncStageHeight(incomingStep);

      const headerEl = document.querySelector('.section-header-pill');
      const headerHeight = headerEl ? headerEl.offsetHeight : 0;
      const sectionTop = this.section.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: Math.max(0, sectionTop - headerHeight), behavior: this.reducedMotion ? 'auto' : 'smooth' });

      this.transitioning = false;
    };

    if (!outgoing || this.reducedMotion) {
      activate();
      return;
    }

    this.transitioning = true;
    outgoing.classList.add('is-leaving');
    outgoing.classList.remove('is-active');

    incoming.hidden = false;
    requestAnimationFrame(() => {
      incoming.classList.add('is-active');
      this.syncStageHeight(incoming);
    });

    window.setTimeout(() => activate(incoming), this.transitionMs);
  }

  syncStageHeight(stepEl) {
    if (!this.stage || !stepEl) return;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (isMobile) {
      // Let the stage grow with content instead of locking to a prior step's box height.
      this.stage.style.minHeight = `${stepEl.scrollHeight}px`;
      return;
    }

    this.stage.style.minHeight = `${stepEl.offsetHeight}px`;
  }

  // Step numbers whose display condition is currently met. Conditional steps
  // (e.g. the CPAP question) drop out of the list — and therefore the pill
  // count — whenever the gating answer doesn't match.
  eligibleSteps() {
    const steps = [];
    for (let i = 1; i <= this.totalSteps; i++) {
      const el = this.section.querySelector(`[data-step="${i}"]`);
      if (el && this.conditionMet(el)) steps.push(i);
    }
    return steps;
  }

  buildPills(count = this.totalSteps) {
    if (!this.progressEl) return;
    this.progressEl.innerHTML = '';
    for (let i = 1; i <= count; i++) {
      const pill = document.createElement('span');
      pill.className = 'quiz-progress__pill';
      pill.dataset.pillStep = i;
      this.progressEl.appendChild(pill);
    }
  }

  updateProgress(step) {
    if (!this.progressEl) return;

    const eligible = this.eligibleSteps();
    if (this.progressEl.children.length !== eligible.length) {
      this.buildPills(eligible.length);
    }

    // Progress position = where the current step sits among eligible steps,
    // so skipped questions never leave a gap in the pills.
    const position = eligible.indexOf(step) + 1;
    this.progressEl.querySelectorAll('.quiz-progress__pill').forEach((pill) => {
      const n = parseInt(pill.dataset.pillStep, 10);
      pill.classList.remove('quiz-progress__pill--done', 'quiz-progress__pill--current');
      if (n < position) pill.classList.add('quiz-progress__pill--done');
      else if (n === position) pill.classList.add('quiz-progress__pill--current');
    });
  }

  assessmentAnswers() {
    return {
      snore_loudly: this.answers.snore ?? '',
      daytime_fatigue: this.answers.tired ?? '',
      observed_stop_breathing: this.answers.observed ?? '',
      high_blood_pressure: this.answers.blood_pressure ?? '',
      diagnosed_sleep_apnea: this.answers.diagnosed ?? '',
      uses_cpap: this.answers.uses_cpap ?? '',
    };
  }

  submitToSleepOps(contactData) {
    try {
      const payload = {
        contact: {
          firstName: contactData.firstName || '',
          lastName: contactData.lastName || '',
          email: contactData.email || '',
          phone: contactData.phone || '',
          birthYear: contactData.birthYear || '',
          state: contactData.state || '',
        },
        answers: { ...this.answers },
      };
      fetch('https://kpztpgyfjbufyiuoiqxy.supabase.co/functions/v1/sleep-assessment', {
        method: 'POST',
        mode: 'cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(payload),
      }).catch((err) => console.warn('[QuizSteps] SleepOps send failed', err));
    } catch (err) {
      console.warn('[QuizSteps] SleepOps send skipped', err);
    }
  }

  submitToKlaviyo(contactData) {
    const contactStep = this.section.querySelector('[data-block-type="contact"]');
    if (!contactStep || contactStep.dataset.klaviyoEnabled !== 'true') return;

    const companyId = contactStep.dataset.klaviyoCompanyId;
    const listId = contactStep.dataset.klaviyoListId;
    const metric = contactStep.dataset.klaviyoMetric || 'Completed Sleep Assessment';
    if (!companyId || !contactData.email) return;

    let digits = String(contactData.phone || '').replace(/\D/g, '');
    if (digits.length === 11 && digits[0] === '1') digits = digits.slice(1);
    const phoneE164 = /^[2-9]\d{2}[2-9]\d{6}$/.test(digits) ? `+1${digits}` : '';
    const now = new Date().toISOString();
    const answers = this.assessmentAnswers();

    const properties = {
      state: contactData.state || undefined,
      birth_year: contactData.birthYear || undefined,
      assessment_completed_at: now,
      assessment_source: 'shopify-theme',
    };
    Object.keys(answers).forEach((key) => {
      if (answers[key] !== '') properties[`assessment_${key}`] = answers[key];
    });

    const profileAttributes = {
      email: contactData.email,
      first_name: contactData.firstName || undefined,
      last_name: contactData.lastName || undefined,
      properties,
    };

    const headers = { 'Content-Type': 'application/json', revision: '2024-10-15' };
    const endpoint = (path) => `https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(companyId)}`;

    // Klaviyo's client subscribe endpoint infers the channel from the identifiers sent:
    // email present → email consent, phone_number present → SMS consent. One request per
    // channel so an SMS rejection (no SMS channel provisioned, bad number) can't block email.
    const subscribeBody = (profile) => {
      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Sleep assessment',
            profile: { data: { type: 'profile', attributes: profile } },
          },
        },
      };
      if (listId) body.data.relationships = { list: { data: { type: 'list', id: listId } } };
      return body;
    };
    const emailSubscribeBody = subscribeBody(profileAttributes);
    const smsSubscribeBody = phoneE164 && contactData.smsConsent
      ? subscribeBody({ email: contactData.email, phone_number: phoneE164 })
      : null;

    const eventBody = {
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: metric } } },
          profile: {
            data: {
              type: 'profile',
              attributes: { email: contactData.email },
            },
          },
          properties: {
            ...answers,
            state: contactData.state || '',
            birth_year: contactData.birthYear || '',
            sms_consent: !!contactData.smsConsent,
            source: 'shopify-theme',
          },
          time: now,
          unique_id: this.klaviyoEventId || (this.klaviyoEventId = `assessment-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`),
        },
      },
    };

    // Without a list, /client/subscriptions rejects the request. Fall back to a plain
    // profile upsert so name + answers still land, then let the event drive flows.
    const profileBody = { data: { type: 'profile', attributes: profileAttributes } };

    const post = (path, body) =>
      fetch(endpoint(path), { method: 'POST', headers, body: JSON.stringify(body), keepalive: true })
        .then(async (res) => {
          if (!res.ok) {
            const text = await res.text().catch(() => '');
            console.warn(`[QuizSteps] Klaviyo ${path} ${res.status}`, text.slice(0, 400));
          }
          return res;
        })
        .catch((err) => {
          console.warn(`[QuizSteps] Klaviyo ${path} failed`, err);
        });

    if (listId) {
      post('subscriptions', emailSubscribeBody);
      if (smsSubscribeBody) post('subscriptions', smsSubscribeBody);
    } else {
      console.warn('[QuizSteps] Klaviyo list ID missing — profile saved without consent');
      post('profiles', profileBody);
    }
    post('events', eventBody);
  }

  populateFormFields() {
    const firstName = this.section.querySelector('#QuizFirstName')?.value.trim() || '';
    const lastName  = this.section.querySelector('#QuizLastName')?.value.trim()  || '';
    const email     = this.section.querySelector('#QuizContactEmail')?.value.trim() || '';
    const phone     = this.section.querySelector('#QuizPhone')?.value.trim()     || '';
    const birthYear = this.section.querySelector('#QuizBirthYear')?.value.trim() || '';
    const state     = this.section.querySelector('#QuizState')?.value || '';
    const smsConsent = !!this.section.querySelector('#QuizSmsConsent')?.checked;
    const fullName  = [firstName, lastName].filter(Boolean).join(' ');

    const nameField = this.section.querySelector('#QuizContactName');
    if (nameField) nameField.value = fullName;

    const lines = [
      'Sleep Assessment Submission',
      '===========================',
      '',
      '— Contact details —',
      `Name:       ${fullName || '—'}`,
      `Email:      ${email || '—'}`,
      `Phone:      ${phone || '—'}`,
      `Birth year: ${birthYear || '—'}`,
      `State:      ${state || '—'}`,
      `SMS consent: ${smsConsent ? 'Yes' : 'No'}`,
      '',
      '— Quiz answers —',
    ];

    this.section.querySelectorAll('.quiz-step[data-answer-key]').forEach((step) => {
      const key   = step.dataset.answerKey;
      const label = step.dataset.answerLabel || key;
      const value = this.answers[key];
      lines.push(`${label}: ${value !== undefined ? value : '—'}`);
    });

    if (this.answers.height !== undefined || this.answers.weight !== undefined) {
      lines.push(`Height: ${this.answers.height || '—'}`);
      lines.push(`Weight: ${this.answers.weight || '—'}`);
    }

    const bodyField = this.section.querySelector('#QuizContactBody');
    if (bodyField) bodyField.value = lines.join('\n');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const section = document.querySelector('#QuizSection');
  if (section) new QuizSteps(section);
});
